import { Button, ButtonProps, Tooltip, useTheme } from '@mui/material';
import React from 'react';

import { colors } from '../theme/theme';

interface CustomButtonProps extends ButtonProps {
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    bgColor?: string;
    borderColor?: string;
    textColor?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    variant?: 'contained' | 'outlined' | 'text';
    children?: string;
    sx?: Record<string, unknown>;
    SMButton?: boolean;
    MDButton?: boolean;
    XSButton?: boolean;
    /** Tooltip text shown when the button is disabled */
    disabledTooltip?: string;
}

const CustomButton: React.FC<CustomButtonProps> = ({
    children,
    variant = 'contained',
    startIcon,
    endIcon,
    bgColor,
    borderColor,
    textColor,
    onClick,
    sx,
    SMButton = false,
    MDButton = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    XSButton: _XSButton = false,
    disabledTooltip,
    disabled,
    fullWidth = true,
    ...props
}) => {
    const theme = useTheme();
    // Shared disabled-state style for the "contained" and "outlined" variants.
    const disabledButtonStyle = {
        borderColor: colors.greyPreset[20],
        backgroundColor: colors.greyPreset[20],
        color: theme.palette.text.disabled,
        cursor: 'not-allowed',
        pointerEvents: 'auto',
        '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
            color: theme.palette.text.disabled,
            '& path': {
                fill: theme.palette.text.disabled,
            },
        },
    };
    // Define dynamic styles based on variant
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const variantStyles: Record<string, any> = {
        contained: {
            backgroundColor: bgColor || colors.primaryLight[100],
            border: `1px solid ${bgColor || colors.primaryLight[100]}`,
            color: textColor || colors.fullWhite,
            '&:hover': {
                backgroundColor: bgColor ? bgColor : colors.primaryLight[100],
                opacity: 0.9,
            },
            '&.Mui-disabled': disabledButtonStyle,
            '& svg': {
                color: textColor || colors.fullWhite,
                '& path': {
                    fill: textColor || colors.fullWhite,
                },
            },
        },
        outlined: {
            border: `1px solid ${borderColor || colors.primaryLight[100]}`,
            color: textColor || colors.primaryLight[100],
            backgroundColor: colors.fullWhite,
            '&:hover': {
                backgroundColor: bgColor || theme.palette.action.hover,
                borderColor: borderColor || colors.primaryLight[100],
            },
            '&.Mui-disabled': disabledButtonStyle,
            '& svg, & span': {
                color: textColor || colors.primaryLight[100],
                '& path': {
                    fill: textColor || colors.primaryLight[100],
                },
            },
        },
        text: {
            color: textColor || colors.primaryLight[100],
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: bgColor || theme.palette.action.hover,
            },
            '&.Mui-disabled': {
                cursor: 'not-allowed',
                pointerEvents: 'auto',
            },
        },
    };

    const buttonSx = {
        ...theme.typography.body1,
        lineHeight: '18px',
        textTransform: 'none',
        borderRadius: 2,
        fontWeight: 600,
        fontSize: 16,
        p: '12.5px',
        minWidth: 'unset',
        ...variantStyles[variant],
        ...sx,
        boxShadow: 'none',
        height: 'fit-content',
        '& .MuiButton-startIcon': {
            '& span': {
                fontSize: 16,
                fontWeight: 600,
            },
        },
        ...(SMButton && {
            ...theme.typography.caption,
            py: '6px',
            lineHeight: 'normal',
            '& .MuiButton-startIcon': {
                '& span': {
                    fontSize: 14,
                    fontWeight: 600,
                },
            },
        }),
        ...(MDButton && {
            ...theme.typography.caption,
            py: '8px',
            lineHeight: 'normal',
            '& .MuiButton-startIcon': {
                '& span': {
                    fontSize: 14,
                    fontWeight: 600,
                },
            },
        }),
        // this below code for feature reference
        // ...(XSButton && {
        //     '& .MuiButton-startIcon': {
        //         '& span': {
        //             fontSize: 16,
        //             fontWeight: 600,
        //         },
        //     },
        //     fontSize: 12,
        //     py: '6.5px',
        //     lineHeight: 'normal',
        // }),
    };

    const button = (
        <Button
            variant={variant}
            startIcon={startIcon}
            endIcon={endIcon}
            onClick={onClick}
            sx={buttonSx}
            disabled={disabled}
            fullWidth={fullWidth}
            {...props}
        >
            {children}
        </Button>
    );

    const showDisabledTooltip = Boolean(disabled && disabledTooltip);

    if (showDisabledTooltip) {
        return (
            <Tooltip title={disabledTooltip} placement="top" arrow>
                <span style={{ display: 'inline-flex' }}>{button}</span>
            </Tooltip>
        );
    }

    return button;
};

export default CustomButton;
